import * as Sentry from "@sentry/react";

function scrubPII(value) {
  return String(value || "")
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[email]")
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, "[ssn]")
    .replace(/\b\d{7,}\b/g, "[number]");
}

const dsn = process.env.REACT_APP_SENTRY_DSN;
const state = {
  initialized: false,
  transport: dsn ? "sentry" : "local-console",
  productionConnected: Boolean(dsn),
};

// Scrubbed ring buffer for the opt-in feedback diagnostics. Kept outside
// `state` so diagnostic snapshots of `state` never embed error text.
let recentErrors = [];

function rememberClientError(error) {
  const message = scrubPII(error?.message || String(error)).slice(0, 240);
  if (!message) return;
  recentErrors = [
    { message, capturedAt: new Date().toISOString() },
    ...recentErrors,
  ].slice(0, 5);
}

export function getRecentClientErrors() {
  return recentErrors.map(({ message, capturedAt }) => `${capturedAt} · ${message}`);
}

export function getClientRuntimeDiagnostics(env = process.env) {
  const required = [
    "REACT_APP_FIREBASE_API_KEY",
    "REACT_APP_FIREBASE_PROJECT_ID",
    "REACT_APP_FIREBASE_APP_ID",
  ];
  const missing = required.filter((key) => !env[key] || /YOUR_|your-project/.test(env[key]));
  return {
    status: missing.length ? "not_ready" : "ready",
    environment: env.REACT_APP_ENV || env.NODE_ENV || "unknown",
    projectId: env.REACT_APP_FIREBASE_PROJECT_ID || "not-configured",
    appCheckConfigured: Boolean(env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY && env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY !== "YOUR_RECAPTCHA_KEY"),
    observability: { ...state },
    missingRequired: missing,
  };
}

export function initClientObservability() {
  if (state.initialized) return { ...state };
  state.initialized = true;
  if (!dsn) {
    // Explicit local/no-op transport. This is not a production monitoring connection.
    console.info("[observability] local-console transport; vendor not production-connected");
    return { ...state };
  }
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0),
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,
    beforeSend(event) {
      if (event.message) event.message = scrubPII(event.message);
      event.exception?.values?.forEach((exception) => {
        if (exception.value) exception.value = scrubPII(exception.value);
      });
      return event;
    },
  });
  return { ...state };
}

export function reportClientError(error, context = {}) {
  rememberClientError(error);
  const safeContext = Object.fromEntries(
    Object.entries(context)
      .filter(([key, value]) => !/(name|email|phone|address|token|secret|document)/i.test(key) && ["string", "number", "boolean"].includes(typeof value))
      .map(([key, value]) => [key, typeof value === "string" ? scrubPII(value).slice(0, 160) : value])
  );
  if (state.productionConnected) {
    Sentry.captureException(error, { extra: safeContext });
  } else {
    console.error("[observability:not-production-connected]", {
      operation: safeContext.operation || "client_runtime",
      outcome: "error",
      error: scrubPII(error?.message || String(error)),
      ...safeContext,
    });
  }
}

export function reportClientEvent(operation, outcome, fields = {}) {
  const payload = { operation, outcome, ...fields };
  if (state.productionConnected) {
    Sentry.addBreadcrumb({ category: "operation", level: outcome === "error" ? "error" : "info", data: payload });
  } else {
    console.info("[observability:not-production-connected]", payload);
  }
}
